import React, { useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { X, CreditCard } from 'lucide-react';
import AppHeader from './AppHeader';
import Footer from './Footer';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Общий шаблон для всех страниц: единые Header и Footer,
 * контент страницы рендерится через <Outlet />.
 */
const formatAccessUntil = (accessUntil: string | null): string => {
  if (!accessUntil) return '';
  try {
    const d = new Date(accessUntil);
    return isNaN(d.getTime()) ? accessUntil : d.toLocaleDateString(undefined, { dateStyle: 'long' });
  } catch {
    return accessUntil;
  }
};

const Layout: React.FC = () => {
  const location = useLocation();
  const mainScrollRef = useRef<HTMLElement | null>(null);
  const { sessionDowngradedMessage, dismissSessionDowngraded, cancelAtPeriodEnd, accessUntil, isPro } = useAuth();
  // Только когда подписка в статусе отмены (cancelAtPeriodEnd из GET /api/auth/me)
  const showCancelBanner = isPro && cancelAtPeriodEnd;
  const hasDowngradeBanner = Boolean(sessionDowngradedMessage?.trim());
  const hasAnyBanner = hasDowngradeBanner || showCancelBanner;

  // Контент скроллится внутри .main-scroll, а не window — сбрасываем позицию на каждом переходе.
  useEffect(() => {
    const node = mainScrollRef.current;
    if (node) {
      node.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.pathname]);

  return (
    <div className="flex flex-1 flex-col min-h-0 w-full overflow-x-hidden min-w-0 max-w-full">
      <AppHeader />
      {/* Один скролл: баннеры — первый контент внутри main, без блока между хедером и main */}
      <main ref={mainScrollRef} className="main-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <div className="main-content-under-header">
          {hasAnyBanner ? (
            <div className="relative z-10">
              {hasDowngradeBanner ? (
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#D4AF37]/15 border-b border-[#D4AF37]/40 text-[#D4AF37] text-sm shrink-0">
                  <span>{sessionDowngradedMessage!.trim()}</span>
                  <button
                    type="button"
                    onClick={dismissSessionDowngraded}
                    className="p-1 rounded hover:bg-white/10 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : null}
              {showCancelBanner ? (
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-[#D4AF37]/25 border-b border-[#D4AF37]/50 text-[#D4AF37] text-sm shrink-0">
                  <span>
                    You cancelled your subscription. PRO access until <strong>{formatAccessUntil(accessUntil) || 'end of billing period'}</strong>.
                  </span>
                  <Link
                    to="/billing"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#D4AF37]/30 hover:bg-[#D4AF37]/40 text-[#D4AF37] font-medium transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    Billing
                  </Link>
                </div>
              ) : null}
            </div>
          ) : null}
        <div className="layout-body">
          <Outlet />
        </div>
        <Footer />
        </div>
      </main>
    </div>
  );
};

export default Layout;
